import type { Book, SideBook } from "../lib/board";
import { cn } from "../lib/cn";
import { fmtLine, fmtOdds, fmtStart, fmtUsd, sideLineText, type OddsFormat } from "../lib/format";
import { useOddsFormat } from "../lib/odds-format";
import { usePriceMode } from "../lib/price-mode";
import type { Contest, Partner } from "../lib/types";
import { ChipRow } from "./ChipRow";
import { Mark } from "./Mark";

export interface LinePicker {
  /** The lines this book can flip to (every line, or the same player's other lines). */
  books: Book[];
  currentId: string;
  /** The main line, marked so the viewer can find their way back. */
  mainId?: string;
  onPick: (bookId: string) => void;
}

/** What a line chip says: the away side's spread, or the shared number for totals and props. */
export function lineChipLabel(book: Book): string {
  const away = book.sides.find((s) => s.sideKey === "away");
  if (away && away.line !== null) return fmtLine("away", away.line);
  return book.line === null ? "—" : String(book.line);
}

/** Every venue's quote for one book, side by side, best first. */
export function BookComparison({ book, partners }: { book: Book | null; partners?: Map<string, Partner> }) {
  const format = useOddsFormat();
  const quoted = book ? book.sides.some((side) => side.quotes.length > 0) : false;
  if (!book || !quoted) {
    return <p className="rounded-2xl bg-cell/50 p-4 text-[13px] text-dim">No venue is quoting this right now.</p>;
  }
  const threeWay = book.sides.length >= 3;
  return (
    <div className={cn("grid gap-2.5", threeWay ? "grid-cols-3" : "grid-cols-2")}>
      {book.sides.map((side) => (
        <SideColumn key={side.sideKey} side={side} format={format} partners={partners} compact={threeWay} />
      ))}
    </div>
  );
}

/** The fine print under a comparison. */
export function ComparisonNotes() {
  const mode = usePriceMode();
  return (
    <div className="space-y-1.5 text-[12px] leading-relaxed text-faint">
      {mode === "taker" && (
        <p>
          Prices include each venue's taker fee — what it costs to take the price right now. Venues that report no fee
          show their listed price.
        </p>
      )}
      <p>
        Each price is the best on that venue's book right now, and the dollar figure is how much it's offering there.
        Prices move — the venue has the final say.
      </p>
    </div>
  );
}

/** The side panel for a board price: its venues, plus its other lines. */
export function VenuePanel({
  contest,
  title,
  marketLabel,
  subject,
  league,
  book,
  lines,
  partners,
  onClose,
}: {
  contest: Contest;
  /** "Away @ Home". */
  title: string;
  marketLabel: string;
  /** The player or team a prop is about. */
  subject?: string | null;
  league?: string;
  book: Book | null;
  lines?: LinePicker | null;
  partners?: Map<string, Partner>;
  onClose: () => void;
}) {
  const s = fmtStart(contest.starts_at);
  // From ANY line that has an away side — the first line can be quoted on one side only.
  const awayLabel = lines?.books.flatMap((b) => b.sides).find((x) => x.sideKey === "away")?.label;

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="brand-text text-[11px] font-bold uppercase tracking-[0.16em]">{marketLabel}</div>
          {subject && <div className="mt-1 font-display text-lg font-bold leading-tight">{subject}</div>}
          <h2 className={cn("font-display font-bold leading-tight", subject ? "mt-0.5 text-[14px] text-dim" : "mt-1 text-xl")}>
            {title}
          </h2>
          <div className="mt-1 text-[12px] text-dim">
            {s.live ? "Live now" : [s.day ?? "Today", s.time].join(" · ")}
            {league ? ` · ${league}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-mr-1 rounded-full p-2 text-dim transition-colors hover:bg-cell hover:text-ink"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {lines && lines.books.length > 1 && (
        <ChipRow
          label={awayLabel ? `Lines · ${awayLabel}` : "Lines"}
          chips={lines.books.map((b) => ({ id: b.id, label: lineChipLabel(b), marked: b.id === lines.mainId }))}
          value={lines.currentId}
          onChange={lines.onPick}
          action={
            lines.mainId && lines.currentId !== lines.mainId
              ? { label: "Main line", onClick: () => lines.onPick(lines.mainId!) }
              : null
          }
        />
      )}

      <BookComparison book={book} partners={partners} />
      <ComparisonNotes />
    </div>
  );
}

function SideColumn({
  side,
  format,
  partners,
  compact,
}: {
  side: SideBook;
  format: OddsFormat;
  partners?: Map<string, Partner>;
  compact: boolean;
}) {
  const top = side.quotes[0];
  const line = sideLineText(side);
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-1 px-1">
        <span className="truncate text-[13px] font-semibold">{side.label}</span>
        {line && <span className="shrink-0 text-[12px] font-semibold text-dim">{line}</span>}
      </div>
      <ol className="flex flex-col gap-1.5">
        {side.quotes.length === 0 && (
          <li className="rounded-xl bg-cell/50 px-3 py-2.5 text-[12px] text-faint">No quotes</li>
        )}
        {side.quotes.map((q) => {
          const isBest = !!top && Math.abs(q.price - top.price) < 1e-9;
          const venue = partners?.get(q.partnerId);
          const name = venue?.name ?? q.partnerName;
          const odds = fmtOdds(q.price, q.american, format);
          return (
            <li
              key={q.partnerId}
              title={`${name} · ${odds} · ${fmtUsd(q.available)}`}
              className={cn("flex min-w-0 items-center gap-2 rounded-xl px-2.5 py-2", isBest ? "best-ring bg-cell-hi" : "bg-cell")}
            >
              <Mark name={name} src={venue?.logo_url} size={compact ? 16 : 20} />
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="text-[14px] font-bold">{odds}</span>
                {!compact && <span className="truncate text-[11px] text-dim">{name}</span>}
              </div>
              {!compact && <span className="ml-auto shrink-0 text-[11px] text-dim">{fmtUsd(q.available)}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Desktop panel before anything is picked. */
export function PanelEmpty() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="brand-gradient flex h-12 w-12 items-center justify-center rounded-2xl shadow-[0_0_30px_-8px_var(--color-accent)]">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
          <rect x="3" y="4" width="16" height="3.2" rx="1.6" fill="#1a0f33" />
          <rect x="3" y="9.4" width="11" height="3.2" rx="1.6" fill="#1a0f33" opacity=".6" />
          <rect x="3" y="14.8" width="7" height="3.2" rx="1.6" fill="#1a0f33" opacity=".35" />
        </svg>
      </div>
      <div className="font-display text-lg font-bold">Pick a price</div>
      <p className="max-w-[16rem] text-[13px] leading-relaxed text-dim">
        Tap any price on the board to see every venue quoting it, side by side — and flip through the other lines.
      </p>
    </div>
  );
}
